import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React from 'react'
import styled from 'styled-components'

import { formatDateTime } from '../../helpers/formatDateTime'
import GetEventTypeColor from '../../helpers/getEventTypeColor'
import { isCurrentlyOn } from '../../helpers/is-currently-on'
import { ExtendedH3, ExtendedH6 } from '../Typography/Typography'
import ArrowLink from '../atoms/arrowLink'
import Link from '../atoms/link'
import theme from '../atoms/theme'
import { Times } from '../organisms/contentSet'
import { CategorySeparator, renderCategories } from '../organisms/heroCarousel'

const { colours, breakpoints, font, animation } = theme

export type ContentSetItemProps = {
  item: Queries.ContentfulEvent | Queries.ContentfulPage
  showLeadingText?: boolean
  withoutSlider?: boolean
}

export const StyledArrowLink = styled(ArrowLink)`
  margin-bottom: 0;
`

export const StyledArrowLinkInContent = styled(StyledArrowLink)`
  opacity: 0;
  position: absolute;
  transition: opacity ${animation.defaultLength} ease-in-out;
`

export const ContentSetLeadingParagraph = styled.p`
  margin-top: 1rem;
  font-size: 1.4rem;

  @media only screen and ${breakpoints.toNormalScreen} {
    display: ${(props): string => (props.showLeadingText ? 'block' : 'none')};
  }
`

interface ColourProps {
  colour: string
}

export const CategoryBlock = styled.div<ColourProps>`
  position: absolute;
  background-color: ${(props): string => `${props.colour}`};
  transform: translate3d(-1.6rem, calc(-100% - 1.6rem), 0);

  h6,
  ${ExtendedH6} {
    margin: 0;
  }
`

export const CategoryLinks = styled.div`
  color: ${colours.light};
  padding: 0.5rem 1.6rem;
  display: block;
`

export const ContentSetItemContent = styled.div<ColourProps>`
  position: relative;
  background-color: ${colours.light};
  border-top: 1rem solid ${(props): string => `${props.colour}`};
  transition: transform ${animation.defaultLength} ease-in-out;
  padding: 1.6rem;

  h3,
  ${ExtendedH3} {
    font-weight: ${font.fontWeightBold};
    margin-bottom: 1rem;
  }
`

const StyledImg = styled(GatsbyImage)`
  display: block;

  picture img {
    transform: scale(1);
    transition: transform ${animation.defaultLength} ease-in-out !important;
  }
`

export const ContentSetItemStyled = styled.div`
  position: relative;
  background-color: ${colours.light};
  overflow: hidden;

  @media only screen and ${breakpoints.fromNormalScreen} {
    flex-basis: 100%;
  }

  h3,
  ${ExtendedH3} {
    transition: color ${animation.defaultLength} ease-in-out;
  }

  img {
    transform: scale(1);
    transition: transform ${animation.defaultLength} ease-in-out !important;
  }

  &:hover {
    h3,
    ${ExtendedH3} {
      color: ${colours.primary};
    }

    ${StyledArrowLinkInContent} {
      opacity: 1;
    }

    ${StyledImg} {
      picture img {
        transform: scale(1.1);
      }
    }

    img {
      transform: scale(1.1);
    }

    ${ContentSetItemContent} {
      transform: translate3d(0, -3rem, 0);
    }
  }
`

export const ContentSetItemWrapper = styled(Link)`
  margin-bottom: 2.5rem;
  text-decoration: none;
  display: block;
  @media only screen and ${breakpoints.toNormalScreen} {
    &:last-child {
      margin-bottom: 0;
    }
  }
`

export const ContentSetItemEvent = (
  props: ContentSetItemProps,
): JSX.Element => {
  const {
    preTitle,
    title: itemTitle,
    id,
    leadingParagraph,
    heroImage,
    imageWithFocalPoint,
    category,
    categories,
    eventType,
    startDate,
    endDate,
    slug,
    customDateText,
  } = props.item

  // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
  let categoryColour

  if (category && category.colour !== undefined) {
    categoryColour = category.colour
  }

  if (eventType) {
    categoryColour = GetEventTypeColor(eventType)
  }

  const categorySlug = category
    ? `${category.pageCategoryParent ? `${category.pageCategoryParent.slug}/${category.slug}` : category.slug}`
    : null

  const computedSlug = categorySlug ? `${categorySlug}/${slug}` : slug

  const formattedDateTime =
    startDate && endDate && formatDateTime(startDate, endDate)

  const isEvent = eventType?.slug === 'event'

  const image = getImage(imageWithFocalPoint?.image) || getImage(heroImage)

  return (
    <ContentSetItemWrapper
      to={eventType ? `/${eventType.slug}s/${slug}/` : `/${computedSlug}/`}
      withoutSlider={props.withoutSlider}
      tabIndex={-1}
    >
      <ContentSetItemStyled key={id}>
        {image && !(imageWithFocalPoint?.image?.url || heroImage?.url) && (
          <StyledImg
            className='image'
            image={image}
            alt={imageWithFocalPoint?.image?.title || heroImage?.title}
          />
        )}
        {imageWithFocalPoint?.image?.url && (
          <img
            className='image'
            src={`${imageWithFocalPoint?.image?.url}?w=480&h=300&fit=fill`}
            alt={itemTitle}
          />
        )}
        {!imageWithFocalPoint?.image?.url && heroImage?.url && (
          <img
            className='image'
            src={`${heroImage?.url}?w=480&h=300&fit=fill`}
            alt={itemTitle}
          />
        )}
        <ContentSetItemContent
          className='content-set-item-content'
          colour={categoryColour}
        >
          <CategoryBlock colour={categoryColour}>
            <ExtendedH6 className='category'>
              <CategoryLinks>
                {eventType && (
                  <CategorySeparator
                    as='span'
                    to={`/${eventType.slug}s/`}
                    tabIndex={-1}
                  >
                    {eventType.title}
                  </CategorySeparator>
                )}
                {!isEvent && category && (
                  <CategorySeparator
                    as='span'
                    to={`/${categorySlug}/`}
                    tabIndex={-1}
                  >
                    {category.title}
                  </CategorySeparator>
                )}
                {isEvent && category && (
                  <CategorySeparator as='span' tabIndex={-1}>
                    {category.title}
                  </CategorySeparator>
                )}
                {isEvent &&
                  categories &&
                  renderCategories(eventType, categories, null, 'span')}
                {isCurrentlyOn(startDate, endDate) && (
                  <CategorySeparator as='span' tabIndex={-1}>
                    Currently on
                  </CategorySeparator>
                )}
              </CategoryLinks>
            </ExtendedH6>
          </CategoryBlock>
          <ExtendedH3>
            {preTitle && (
              <>
                {preTitle}
                <br />
              </>
            )}
            {itemTitle}
          </ExtendedH3>
          {leadingParagraph && (
            <ContentSetLeadingParagraph showLeadingText={props.showLeadingText}>
              {leadingParagraph?.childMarkdownRemark?.rawMarkdownBody}
            </ContentSetLeadingParagraph>
          )}
          {customDateText ? (
            <Times>{customDateText}</Times>
          ) : (
            formattedDateTime && <Times>{formattedDateTime}</Times>
          )}
          <StyledArrowLinkInContent>View more</StyledArrowLinkInContent>
        </ContentSetItemContent>
      </ContentSetItemStyled>
    </ContentSetItemWrapper>
  )
}
